import type { LeadFields } from "@/lib/types";

/**
 * Create new contact on the Phone page (ACL-626): another person in the lead's accident, a passenger
 * say, typed in while the lead is still on the line. The New contact form opens with the lead's Date of
 * Accident and City Of Accident, by the keys Send contract reads them by (platform
 * `contract_service.py`), and saving links the new contact to the lead's accident in the same request
 * (platform ADR-203). Pure, for `test/call-new-contact.test.ts`.
 */

export const ACCIDENT_DATE_KEY = "date_of_incident";
export const ACCIDENT_CITY_KEY = "city_of_accident";

/** One of the lead's accident answers the new contact starts with. */
export interface AccidentAnswer {
  key: string;
  /** The lead's value, as the form's box holds it: "" when they have none yet. */
  value: string;
}

/** What the New contact form is given to make another person of this lead's accident. */
export interface SameAccident {
  leadId: string;
  /** The lead's name, for the form's line; null when the record has none. */
  name: string | null;
  /** Null when the lead's form has no such field or the person may not change it: the box is then
   *  left out, so the save never names a key core-api would refuse. */
  date: AccidentAnswer | null;
  city: AccidentAnswer | null;
}

/** The lead's accident date and city, from `GET /leads/{id}/fields`. A form not read yet, or one
 *  without the field, gives null. */
export function accidentAnswers(fields: LeadFields | undefined): Pick<SameAccident, "date" | "city"> {
  const answer = (key: string): AccidentAnswer | null => {
    for (const section of fields?.sections ?? []) {
      const found = section.fields.find((f) => f.key === key);
      if (found) return found.can_edit ? { key, value: typeof found.value === "string" ? found.value : "" } : null;
    }
    return null;
  };
  return { date: answer(ACCIDENT_DATE_KEY), city: answer(ACCIDENT_CITY_KEY) };
}

/** The answers the save sends, from the boxes the form showed. A box left empty sends nothing: an
 *  empty answer is no answer, and a required field may not be sent empty. */
export function accidentFieldsInput(
  accident: SameAccident,
  typed: { date: string; city: string },
): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  if (accident.date && typed.date.trim()) out[accident.date.key] = typed.date.trim();
  if (accident.city && typed.city.trim()) out[accident.city.key] = typed.city.trim();
  return Object.keys(out).length > 0 ? out : undefined;
}
